import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Button, Modal, Typography } from '@mui/material'
import { ChatMajor, DuplicateMinor, EmailMajor } from '@shopify/polaris-icons'
import { showToast } from 'src/utils/toast'

const EMAIL = 'help@gigsafe.com'
const PHONE = '833-789-8388'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const GetHelpModalComponent = ({ isOpen, onClose }: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const handleCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(EMAIL)

    showToast('Copied!', { variant: 'success' })
  }, [])

  const handleCopyPhone = useCallback(() => {
    navigator.clipboard.writeText(PHONE)

    showToast('Copied!', { variant: 'success' })
  }, [])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex flex-col justify-end xsm:justify-center items-center backdrop-blur`}
    >
      <div
        css={tw`flex-col p-4 pt-6 bg-shades-0 rounded-2xl rounded-b-none xsm:rounded-b-2xl w-full xsm:w-[390px]`}
      >
        <Typography variant="h2">Get help now!</Typography>
        <Typography css={tw`mt-2 mb-6 text-shades-400`}>
          Choose your preferred option to reach us.
        </Typography>
        <div css={tw`bg-shades-100 h-[1px] w-full`} />
        <div css={tw`flex flex-row justify-between items-center px-2 py-4`}>
          <div css={tw`flex flex-row items-center`}>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mr-4`}
            >
              <EmailMajor width={20} />
            </div>
            <div>
              <Typography css={tw`mb-0.5`} variant="h4">
                Via email
              </Typography>
              <Typography css={tw`text-shades-400`}>{EMAIL}</Typography>
            </div>
          </div>
          <Button
            css={tw`bg-primary-100 h-[36px] w-[84px] hover:bg-primary-200`}
            onClick={handleCopyEmail}
            data-testid="sign-in-get-help-copy-email-btn"
          >
            <div css={tw`items-center flex flex-row`}>
              <Typography css={tw`mr-1 text-primary-500`} variant="subtitle1">
                Copy
              </Typography>
              <DuplicateMinor width={20} css={tw`fill-primary-500`} />
            </div>
          </Button>
        </div>
        <div css={tw`bg-shades-100 h-[1px] w-full`} />
        <div css={tw`flex flex-row justify-between items-center px-2 py-4`}>
          <div css={tw`flex flex-row items-center`}>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mr-4`}
            >
              <ChatMajor width={20} />
            </div>
            <div>
              <Typography css={tw`mb-0.5 max-w-[140px]`} variant="h4">
                Via phone number (text only)
              </Typography>
              <Typography css={tw`text-shades-400`}>{PHONE}</Typography>
            </div>
          </div>
          <Button
            css={tw`bg-primary-100 h-[36px] w-[84px] hover:bg-primary-200`}
            onClick={handleCopyPhone}
            data-testid="sign-in-get-help-copy-phone-btn"
          >
            <div css={tw`items-center flex flex-row`}>
              <Typography css={tw`mr-1 text-primary-500`} variant="subtitle1">
                Copy
              </Typography>
              <DuplicateMinor width={20} css={tw`fill-primary-500`} />
            </div>
          </Button>
        </div>
        <div css={tw`bg-shades-100 h-[1px] w-full`} />
        <Button
          fullWidth
          onClick={onClose}
          variant="contained"
          css={tw`bg-shades-1000 mt-6`}
          data-testid="sign-in-get-help-close-btn"
        >
          <Typography css={tw`text-shades-0`} fontWeight={600}>
            Close
          </Typography>
        </Button>
      </div>
    </Modal>
  )
}

export const GetHelpModal = memo(GetHelpModalComponent)
